class ExpandableCardSlider {
    constructor(container) {
        this.container = container;
        this.slider = container.querySelector('[data-cards-slider]');
        this.progressBar = container.querySelector('[data-progress-bar]');
        this.prevBtn = container.querySelector('[data-prev-btn]');
        this.nextBtn = container.querySelector('[data-next-btn]');
        this.modal = document.querySelector('[data-card-modal]');
        
        if (!this.slider) return;
        
        this.currentIndex = 0;
        this.cards = Array.from(this.slider.children);
        this.totalCards = this.cards.length;
        this.expandedCard = null;
        this.expandedCardIndex = null;
        this.isAnimating = false;
        
        this.init();
    }

    init() {
        this.calculateDimensions();
        this.bindEvents();
        this.setupCards();
        
        requestAnimationFrame(() => {
            this.updateProgressBar();
            this.updateButtons();
        });
    }

    calculateDimensions() {
        this.isMobile = window.innerWidth < 820;
        this.itemsPerView = this.isMobile ? 1 : 3; // Mobile: 1 card at a time, Desktop: 3 cards
        
        if (this.cards.length > 0) {
            if (this.isMobile) {
                // Mobile: Fixed width for 312px cards
                this.itemWidth = 312;
                this.gap = 16;
            } else {
                // Desktop: Calculate from actual card dimensions
                const firstCard = this.cards.find(card => !card.classList.contains('expanded'));
                if (firstCard) {
                    this.itemWidth = firstCard.offsetWidth;
                    
                    const cardsStyle = window.getComputedStyle(this.slider);
                    this.gap = parseInt(cardsStyle.gap) || 38;
                }
            }
        }
    }

    setupCards() {
        console.log('Setting up cards, total cards:', this.cards.length);
        this.cards.forEach((card, index) => {
            const expandBtn = card.querySelector('[data-expand-card]');
            const closeBtn = card.querySelector('[data-close-card]');
            
            console.log('Card ' + index + ':', {
                expandBtn: !!expandBtn,
                closeBtn: !!closeBtn,
                cardElement: card
            });
            
            if (expandBtn) {
                expandBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    console.log('Expand button clicked for card', index);
                    this.expandCard(card, index);
                });
            } else {
                console.log('No expand button found for card', index);
            }
            
            if (closeBtn) {
                console.log('Setting up close button for card', index, closeBtn);
                closeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    console.log('Close button clicked!', e.target);
                    this.closeCard();
                });
                
                // Add a test click handler to see if button is responsive
                closeBtn.addEventListener('mousedown', () => {
                    console.log('Close button mousedown detected');
                });
            } else {
                console.log('No close button found for card', index);
            }
        });
    }

    expandCard(card, cardIndex) {
        if (this.isAnimating) return;
        
        if (this.isMobile) {
            this.openMobileModal(card);
            return;
        }
        
        // Close any currently expanded card
        if (this.expandedCard && this.expandedCard !== card) {
            this.closeCard();
            return;
        }
        
        this.isAnimating = true;
        this.expandedCard = card;
        this.expandedCardIndex = cardIndex;
        
        // Move the slider so the expanded card is at the left edge
        this.currentIndex = cardIndex;
        
        // Add expanded class
        card.classList.add('expanded');
        
        // Recalculate dimensions and update slider position
        setTimeout(() => {
            this.calculateDimensions();
            this.updateSlider();
            this.updateProgressBar();
            this.updateButtons();
            this.isAnimating = false;
        }, 100);
    }

    closeCard() {
        if (!this.expandedCard || this.isAnimating) return;
        
        this.isAnimating = true;
        
        // Remove expanded class
        this.expandedCard.classList.remove('expanded');
        
        // Reset expanded card reference
        this.expandedCard = null;
        this.expandedCardIndex = null;
        
        // Recalculate dimensions and update slider position
        setTimeout(() => {
            this.calculateDimensions();
            this.updateSlider();
            this.updateProgressBar();
            this.updateButtons();
            this.isAnimating = false;
        }, 100);
    }

    openMobileModal(card) {
        if (!this.modal) return;
        
        // Get card data using the new HTML structure with safe property access
        const headingElement = card.querySelector('.card__heading');
        const title = headingElement ? headingElement.textContent : '';
        
        const image = card.style.backgroundImage;
        
        const descriptionElement = card.querySelector('.card__description');
        const description = descriptionElement ? descriptionElement.innerHTML : '';
        
        const link = card.querySelector('.card__link');
        const statistic = card.querySelector('.card__main-content-statistic');
        
        // Populate modal
        const modalTitle = this.modal.querySelector('[data-modal-title]');
        const modalImage = this.modal.querySelector('[data-modal-image]');
        const modalDescription = this.modal.querySelector('[data-modal-description]');
        const modalLink = this.modal.querySelector('[data-modal-link]');
        const modalStats = this.modal.querySelector('[data-modal-stats]');
        
        if (modalTitle) modalTitle.textContent = title;
        
        if (modalImage && image) {
            // Extract URL from background-image style
            const imageUrl = image.match(/url\(["']?([^"']*)["']?\)/);
            if (imageUrl && imageUrl[1]) {
                modalImage.src = imageUrl[1];
                modalImage.alt = title;
            }
        }
        
        if (modalDescription) modalDescription.innerHTML = description;
        
        if (modalLink && link) {
            modalLink.href = link.href;
            modalLink.textContent = link.textContent;
            modalLink.target = link.target;
            modalLink.style.display = 'inline-flex';
        } else if (modalLink) {
            modalLink.style.display = 'none';
        }
        
        if (modalStats && statistic) {
            modalStats.innerHTML = statistic.innerHTML;
            modalStats.style.display = 'flex';
        } else if (modalStats) {
            modalStats.style.display = 'none';
        }
        
        // Show modal
        this.modal.classList.add('active');
        document.body.style.overflow = 'hidden';
        
        // Setup close functionality
        this.setupModalClose();
    }

    setupModalClose() {
        if (!this.modal) return;
        
        const closeBtn = this.modal.querySelector('[data-modal-close]');
        
        const closeModal = () => {
            this.modal.classList.remove('active');
            document.body.style.overflow = '';
        };
        
        // Close button click
        if (closeBtn) {
            closeBtn.addEventListener('click', closeModal, { once: true });
        }
        
        // Click outside modal
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                closeModal();
            }
        }, { once: true });
        
        // Escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);
    }

    bindEvents() {
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.next();
            });
        }
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.prev();
            });
        }
        
        this.addTouchSupport();
        
        window.addEventListener('resize', () => this.handleResize());
    }

    addTouchSupport() {
        const touchTarget = this.slider.parentElement || this.slider;
        
        if (!touchTarget) return;

        let startX = null;
        let startY = null;
        let currentX = null;
        let currentY = null;
        let isPointerDown = false;
        let isHorizontalSwipe = false;
        let hasTouchSupport = 'ontouchstart' in window;

        const resetState = () => {
            startX = null;
            startY = null;
            currentX = null;
            currentY = null;
            isPointerDown = false;
            isHorizontalSwipe = false;
        };

        const handleStart = (clientX, clientY) => {
            startX = clientX;
            startY = clientY;
            currentX = clientX;
            currentY = clientY;
            isPointerDown = true;
            isHorizontalSwipe = false;
        };

        const handleMove = (clientX, clientY) => {
            if (!isPointerDown || startX === null || startY === null) {
                return;
            }

            currentX = clientX;
            currentY = clientY;

            const diffX = currentX - startX;
            const diffY = currentY - startY;

            if (!isHorizontalSwipe && (Math.abs(diffX) > 15 || Math.abs(diffY) > 15)) {
                isHorizontalSwipe = Math.abs(diffX) > Math.abs(diffY);
            }
        };

        const handleEnd = () => {
            if (!isPointerDown || startX === null || currentX === null) {
                resetState();
                return;
            }

            const diffX = currentX - startX;

            if (isHorizontalSwipe && Math.abs(diffX) > 50) {
                if (diffX > 0) {
                    this.prev();
                } else {
                    this.next();
                }
            }

            resetState();
        };

        touchTarget.addEventListener('touchstart', (e) => {
            const touch = e.touches[0];
            handleStart(touch.clientX, touch.clientY);
        }, { passive: true });

        touchTarget.addEventListener('touchmove', (e) => {
            if (!isPointerDown) return;
            const touch = e.touches[0];
            handleMove(touch.clientX, touch.clientY);
            
            if (isHorizontalSwipe) {
                e.preventDefault();
            }
        }, { passive: false });

        touchTarget.addEventListener('touchend', () => {
            handleEnd();
        }, { passive: true });

        touchTarget.addEventListener('touchcancel', () => {
            resetState();
        }, { passive: true });

        if (!hasTouchSupport) {
            touchTarget.addEventListener('mousedown', (e) => {
                handleStart(e.clientX, e.clientY);
                e.preventDefault();
            });

            touchTarget.addEventListener('mousemove', (e) => {
                if (!isPointerDown) return;
                handleMove(e.clientX, e.clientY);
            });

            touchTarget.addEventListener('mouseup', () => {
                if (!isPointerDown) return;
                handleEnd();
            });

            touchTarget.addEventListener('mouseleave', () => {
                if (isPointerDown) {
                    handleEnd();
                }
            });
        }
    }

    handleResize() {
        const wasMobile = this.isMobile;
        this.calculateDimensions();
        
        // Close expanded card on resize
        if (this.expandedCard) {
            this.closeCard();
        }
        
        // Close mobile modal if switching to desktop
        if (wasMobile && !this.isMobile && this.modal && this.modal.classList.contains('active')) {
            this.modal.classList.remove('active');
            document.body.style.overflow = '';
        }
        
        if (wasMobile !== this.isMobile) {
            this.currentIndex = 0;
            this.updateSlider();
        }
        
        this.updateProgressBar();
        this.updateButtons();
    }

    next() {
        if (this.isAnimating) return;
        
        // Close expanded card when navigating
        if (this.expandedCard) {
            this.closeCard();
            return; // Let the close animation complete first
        }
        
        const maxIndex = Math.max(0, this.totalCards - this.itemsPerView);
        
        if (this.isMobile) {
            // Mobile: Move one card at a time
            if (this.currentIndex < maxIndex) {
                this.currentIndex++;
                this.updateSlider();
                this.updateProgressBar();
                this.updateButtons();
            }
        } else {
            // Desktop: Move by itemsPerView
            const remainingItems = this.totalCards - this.currentIndex - this.itemsPerView;
            
            if (remainingItems > 0) {
                const itemsToMove = Math.min(this.itemsPerView, remainingItems);
                this.currentIndex = Math.min(maxIndex, this.currentIndex + itemsToMove);
                this.updateSlider();
                this.updateProgressBar();
                this.updateButtons();
            }
        }
    }

    prev() {
        if (this.isAnimating) return;
        
        // Close expanded card when navigating
        if (this.expandedCard) {
            this.closeCard();
            return; // Let the close animation complete first
        }
        
        if (this.isMobile) {
            // Mobile: Move one card at a time
            if (this.currentIndex > 0) {
                this.currentIndex--;
                this.updateSlider();
                this.updateProgressBar();
                this.updateButtons();
            }
        } else {
            // Desktop: Move by itemsPerView
            if (this.currentIndex > 0) {
                const itemsToMove = Math.min(this.itemsPerView, this.currentIndex);
                this.currentIndex = Math.max(0, this.currentIndex - itemsToMove);
                this.updateSlider();
                this.updateProgressBar();
                this.updateButtons();
            }
        }
    }

    updateSlider() {
        if (!this.itemWidth) {
            return;
        }
        
        const translateX = -(this.currentIndex * (this.itemWidth + this.gap));
        this.slider.style.transform = 'translateX(' + translateX + 'px)';
    }

    updateProgressBar() {
        if (!this.progressBar) return;
        
        if (this.isMobile) {
            // Mobile: Progress based on single card navigation
            const progressPercentage = this.totalCards > 1 ? ((this.currentIndex + 1) / this.totalCards) * 100 : 100;
            this.progressBar.style.width = progressPercentage + '%';
        } else {
            // Desktop: Original logic
            const lastVisibleItemIndex = this.currentIndex + this.itemsPerView - 1;
            const itemsSeenFromStart = Math.min(lastVisibleItemIndex + 1, this.totalCards);
            const progressPercentage = (itemsSeenFromStart / this.totalCards) * 100;
            this.progressBar.style.width = progressPercentage + '%';
        }
    }

    updateButtons() {
        if (this.prevBtn) {
            this.prevBtn.disabled = this.currentIndex === 0;
        }
        if (this.nextBtn) {
            if (this.isMobile) {
                // Mobile: Disable when at last card
                this.nextBtn.disabled = this.currentIndex >= this.totalCards - 1;
            } else {
                // Desktop: Original logic
                this.nextBtn.disabled = this.currentIndex >= this.totalCards - this.itemsPerView;
            }
        }
    }
}

function initExpandableCardSliders() {
    const sliders = document.querySelectorAll('[data-expandable-slider]');
    sliders.forEach(slider => new ExpandableCardSlider(slider));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExpandableCardSliders);
} else {
    initExpandableCardSliders();
}